import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { findTaskEvidenceStates, Task, TaskEvidenceKind, TaskEvidenceOutcome } from '@jms/model';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { DateTimeFormatPipe } from '@workbuddy/ui/fields/luxon.calendar';
import { injectDialog } from '@workbuddy/ui/v2/blocks/dialog';
import { ButtonComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { HoverCardComponent, HoverCardRow } from '@workbuddy/ui/v2/components/hover.card';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { EvidenceExceptionAction, isEvidenceExceptionReadonly, resolveTaskEvidenceExceptionAction } from '../evidence.exception.policy';
import { buildEvidenceExceptionRows } from '../evidence.hover.content';
import { TaskEvidenceExceptionDialogComponent } from '../task.evidence.exception.dialog';
import { TaskEvidenceOutcomeBadgeComponent } from '../task.evidence.outcome.badge';
import { taskEvidenceStateBridgeStyles, taskEvidenceStateStyleId } from './task.evidence.state.theme';

const ACTION_LABELS: Record<EvidenceExceptionAction, string> = { record: 'Unable to capture', edit: 'Edit exception', view: 'View exception' };

/** One piece of evidence's state, or without `kind` the task's outcome with the task's one exception action; badges only show a hover card, and `changed` fires after a write so the host rereads the task. */
@Component({
  selector: 'task-evidence-state',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [TooltipModule, ButtonComponent, HoverCardComponent, TaskEvidenceOutcomeBadgeComponent],
  template: `
    @if ($state(); as state) {
      @if ($exceptionRows(); as rows) {
        <span class="exception-badge" [pTooltip]="details" [autoHide]="false" [showDelay]="200" tooltipPosition="top" tooltipStyleClass="wb-dark-tooltip" appendTo="body">
          <task-evidence-outcome-badge [outcome]="state" />
        </span>
        <ng-template #details><wb-hover-card title="Exception" [rows]="rows" /></ng-template>
      } @else {
        <task-evidence-outcome-badge [outcome]="state" />
      }
    }
    @if ($actionLabel(); as label) {
      <wb-button class="exception-action" [label]="label" [size]="ButtonSize.Small" [displayStyle]="ButtonDisplayStyle.Tertiary" (onClick)="open()" />
    }
  `,
  styleUrls: ['./task.evidence.state.component.less'],
  host: {
    // Hosts put this inside a header that collapses on click.
    '(click)': '$event.stopPropagation()',
  },
})
export class TaskEvidenceStateComponent {

  private readonly document = inject(DOCUMENT);
  private readonly dialogs = injectDialog();
  private readonly generalSetting = inject(GeneralSettingService);
  private readonly dates = new DateTimeFormatPipe();

  public readonly $task = input<Task | null | undefined>(undefined, { alias: 'task' });
  public readonly $kind = input<TaskEvidenceKind | undefined>(undefined, { alias: 'kind' });
  public readonly $canAddEvidence = input(false, { alias: 'canAddEvidence' });
  public readonly $timezone = input<string | undefined>(undefined, { alias: 'timezone' });
  public readonly changed = output<void>();

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly dateFormat = `${this.generalSetting.generalSettings?.region?.dateFormat ?? 'dd/MM/yyyy'} ${this.generalSetting.generalSettings?.region?.timeFormat ?? 'HH:mm'}`;
  protected readonly $state = computed(() => {
    const task = this.$task();
    const kind = this.$kind();
    if (!kind) return task?.evidenceOutcome;
    return task ? findTaskEvidenceStates(task).find(evidence => evidence.kind === kind)?.state : undefined;
  });
  protected readonly $exceptionRows = computed<HoverCardRow[] | undefined>(() => {
    const exception = this.$state() === TaskEvidenceOutcome.Exception ? this.$task()?.evidenceException : undefined;
    return exception ? buildEvidenceExceptionRows(exception, value => this.dates.transform(value, this.$timezone(), this.dateFormat)) : undefined;
  });
  // A task has one exception, so the action sits once on the heading, not on each collection (Cong 08/10).
  protected readonly $actionLabel = computed(() => {
    const action = this.$kind() ? undefined : resolveTaskEvidenceExceptionAction(this.$task(), this.$canAddEvidence());
    return action ? ACTION_LABELS[action] : undefined;
  });

  public constructor() {

    DesignSystem.Mount(this.document, taskEvidenceStateStyleId, taskEvidenceStateBridgeStyles);
  }

  protected async open(): Promise<void> {

    const task = this.$task();
    if (!task?._id) return;
    let busy = false;
    const readonly = isEvidenceExceptionReadonly(task, this.$canAddEvidence());
    const changed = await this.dialogs.modal<boolean>(TaskEvidenceExceptionDialogComponent, {
      width: 'min(480px, calc(100vw - 40px))',
      inputs: { task, readonly, timezone: this.$timezone() },
      outputs: { busyChange: value => busy = value === true },
      // Escape during a write would resolve with no result, so a successful write would never refresh the page; a write's own close carries a result.
      close: { beforeClose: (_reason, result) => result !== undefined || !busy },
      aria: { label: readonly ? 'Evidence exception' : 'Unable to capture' },
    });
    if (changed) this.changed.emit();
  }
}
